import type { ReactI13nPlugin } from 'react-i13n';
import debounce from 'lodash/debounce';

import config, { getRoute } from 'Config';
import {
  I13N_ACTION_CLICK,
  I13N_ACTION_VIEW,
  I13N_CATEGORY_APP,
  I13N_TYPE_CLEAN_UP,
  I13N_TYPE_CLICK,
  I13N_TYPE_ENHANCED_ECOMMERCE,
  I13N_TYPE_FEATURE_COUNT,
  I13N_TYPE_PAGE_VIEW,
  I13N_TYPE_SET_VALUE,
  I13N_TYPE_VIEW,
} from 'Lib/constants/i13nSpec';
import { debugI13nGTag as debug } from 'Lib/debug';
import type { I13nPluginClass } from 'Lib/i13n';
import requestIdleCallbackManager from 'Lib/requestIdleCallbackManager';
import eventEmitter, { PRIVACY_SETTINGS_CONFIRMED } from 'Lib/utils/emitter';
import isUndefined from 'Lib/utils/isUndefined';
import { pick } from 'Lib/utils/lodashUtils';
import { stripID } from 'Lib/utils/pathUtils';

import { KEY_GOOGLE_FLOODLIGHT } from 'Store/privacy/constants';

import type { LogLevel } from 'EventModels/common/log';

const DEFAULT_CATEGORY = I13N_CATEGORY_APP;
const DEFAULT_LABEL = '';
const DEFAULT_VALUE = 1;

const shouldSample = (rate: number) => Math.random() < rate;

export type PayloadType = {
  action?: string;
  category?: string;
  conversion?: any;
  label: string;
  message?: string;
  non_interaction?: boolean;
  value: number;
  error?: {
    error?: Error;
    level?: LogLevel;
    tags?: string[];
  };
};

function getEventPayload({
  category = DEFAULT_CATEGORY,
  label = DEFAULT_LABEL,
  value = DEFAULT_VALUE,
  ...restProps
}: PayloadType) {
  return {
    ...pick(restProps, ['action', 'non_interaction']),
    event_category: category,
    event_label: label,
    value,
  };
}

const formatMessage = (args: (string | undefined)[]) => args.filter(Boolean).join(' - ');
const { gaPropertyID, gCrossMediaFloodlightID } = config;

class ReactI13nGTag implements I13nPluginClass {
  privacyPreferences: Record<string, boolean>;

  constructor() {
    this.privacyPreferences = {};
  }

  getPlugin = () => {
    if (!__CLIENT__) {
      // TODO: handle properly or don't call in server context
      return {} as ReactI13nPlugin;
    }

    const isCrossMediaSignalable = () =>
      gCrossMediaFloodlightID && !!this.privacyPreferences[KEY_GOOGLE_FLOODLIGHT];

    eventEmitter.on(PRIVACY_SETTINGS_CONFIRMED, (newPrivacyPreferences) => {
      this.privacyPreferences = newPrivacyPreferences;
    });

    return {
      name: 'gtag',
      eventHandlers: {
        [I13N_TYPE_CLEAN_UP]: () => {
          if (!__CLIENT__ || isUndefined(gtag)) {
            return;
          }

          // Remove doubleclick scripts from polluting the DOM
          requestIdleCallbackManager.addTask(() => {
            const scripts = document.querySelectorAll('head > script[src*=doubleclick]');
            [...scripts].forEach((script) => {
              if (document.head) {
                document.head.removeChild(script);
              }
            });
          });

          // Remove doubleclick iframes from polluting the DOM
          requestIdleCallbackManager.addTask(() => {
            const iframes = document.querySelectorAll('body > iframe[src*=doubleclick]');
            [...iframes].forEach((iframe) => {
              if (document.body) {
                document.body.removeChild(iframe);
              }
            });
          });
        },

        [I13N_TYPE_PAGE_VIEW]: debounce((payload: { page: string }) => {
          if (!__CLIENT__ || isUndefined(gtag)) {
            return;
          }
          const { page } = payload;
          const cleanPath = stripID(page);

          requestIdleCallbackManager.addTask(() => {
            debug('Sending page view', cleanPath);
            gtag('config', gaPropertyID, {
              page_location: cleanPath,
              page_path: cleanPath,
            });

            if (isCrossMediaSignalable() && getRoute('login') === cleanPath) {
              gtag('event', 'conversion', {
                allow_custom_scripts: true,
                send_to: `${gCrossMediaFloodlightID}/retarget/allpages+standard`,
              });
            }
          });
        }),

        [I13N_TYPE_CLICK]: (payload: {
          target: any;
          i13nNode: {
            getMergedModel: () => PayloadType;
            getText: (target: any) => string;
          };
        }) => {
          if (!__CLIENT__ || isUndefined(gtag)) {
            return;
          }
          const { i13nNode } = payload;
          if (i13nNode) {
            const { category, conversion, label, message, value } = i13nNode.getMergedModel();
            const gaParams = getEventPayload({
              category,
              label: formatMessage([label, message]) || i13nNode.getText(payload.target),
              value,
            });
            debug('Sending click event', gaParams);
            requestIdleCallbackManager.addTask(() => gtag('event', I13N_ACTION_CLICK, gaParams));
            if (!conversion || (conversion?.isCrossMedia && !isCrossMediaSignalable())) return;
            requestIdleCallbackManager.addTask(() => {
              gtag('event', 'conversion', conversion);
            });
          } else {
            debug('No i13n node found');
          }
        },

        [I13N_TYPE_FEATURE_COUNT]: (payload?: Record<string, any>) => {
          if (!payload || isUndefined(gtag)) {
            return;
          }
          const { action, label, message, value, sampleRate } = payload;

          if (sampleRate && !shouldSample(sampleRate)) {
            return;
          }
          const params = getEventPayload({
            ...payload,
            label: formatMessage([label, message, sampleRate]),
            value,
          });

          debug('Sending count', params);
          requestIdleCallbackManager.addTask(() => {
            gtag('event', action, params);
          });
        },

        [I13N_TYPE_VIEW]: (payload: PayloadType) => {
          if (!__CLIENT__ || !payload || isUndefined(gtag)) {
            return;
          }
          const params = getEventPayload({
            ...payload,
            label: formatMessage([payload.label, payload.message]),
            value: 1,
          });
          debug('Sending view event', params);
          requestIdleCallbackManager.addTask(() => gtag('event', I13N_ACTION_VIEW, params));
        },

        [I13N_TYPE_SET_VALUE]: (payload: { key: string; value: string }): void => {
          if (!__CLIENT__ || !payload || isUndefined(gtag)) {
            return;
          }

          const { key, value } = payload;

          if (key && value) {
            debug('Set', key, 'to', value);
            gtag('set', {
              [key]: value,
            });
          }
        },

        [I13N_TYPE_ENHANCED_ECOMMERCE]: (payload: any): void => {
          if (!__CLIENT__ || !payload || isUndefined(gtag)) {
            return;
          }
          debug('Sending enhanced e-commerce event', payload);
          const { action, ...restProps } = payload;
          requestIdleCallbackManager.addTask(() => {
            gtag('event', action, restProps);
          });
        },
      },
    };
  };
}

export default ReactI13nGTag;
